import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Download, Save, Calculator, TrendingUp, DollarSign, Clock } from 'lucide-react';

// Gerador de PDF inline (sem dependência externa)
function generateSimulationPDF(formData: any, result: any, userName?: string) {
  const { jsPDF } = (window as any).jspdf;
  if (!jsPDF) { alert('Biblioteca PDF não carregada. Tente novamente.'); return; }

  const doc = new jsPDF();
  const green = [5, 150, 105];
  const dark = [17, 24, 39];
  const gray = [107, 114, 128];
  const lightGray = [243, 244, 246];

  const fmt = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  const today = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date());

  // Header
  doc.setFillColor(...green as [number,number,number]);
  doc.rect(0, 0, 210, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Inteligência Financeira', 14, 14);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Simulação de Financiamento — BNDES Automático', 14, 22);
  doc.setFontSize(9);
  doc.text(`Gerado em ${today}${userName ? ' · ' + userName : ''}`, 14, 29);

  let y = 44;

  // Título
  doc.setTextColor(...dark as [number,number,number]);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Resultado da Simulação', 14, y);
  y += 10;

  // Card valor total
  doc.setFillColor(...lightGray as [number,number,number]);
  doc.roundedRect(14, y, 182, 22, 3, 3, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray as [number,number,number]);
  doc.text('Valor Total do Projeto', 20, y + 8);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark as [number,number,number]);
  doc.text(fmt(result.totalValue), 20, y + 18);
  y += 30;

  // Financiamento e Capital de Giro lado a lado
  doc.setFillColor(239, 246, 255);
  doc.roundedRect(14, y, 88, 28, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(37, 99, 235);
  doc.text('Financiamento (70%)', 20, y + 9);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(29, 78, 216);
  doc.text(fmt(result.financingValue), 20, y + 20);

  doc.setFillColor(236, 254, 255);
  doc.roundedRect(108, y, 88, 28, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(8, 145, 178);
  doc.text('Capital de Giro (30%)', 114, y + 9);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(7, 89, 133);
  doc.text(fmt(result.workingCapitalValue), 114, y + 20);
  y += 36;

  // Parcela
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(14, y, 182, 28, 3, 3, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...green as [number,number,number]);
  doc.text('Parcela Mensal Estimada', 20, y + 9);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 95, 70);
  doc.text(fmt(result.monthlyPayment), 20, y + 21);
  y += 36;

  // Juros e Payback
  doc.setFillColor(...lightGray as [number,number,number]);
  doc.roundedRect(14, y, 88, 24, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray as [number,number,number]);
  doc.text('Total de Juros', 20, y + 9);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark as [number,number,number]);
  doc.text(fmt(result.totalInterest), 20, y + 19);

  doc.setFillColor(...lightGray as [number,number,number]);
  doc.roundedRect(108, y, 88, 24, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray as [number,number,number]);
  doc.text('Payback', 114, y + 9);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark as [number,number,number]);
  doc.text(`${result.paybackPeriod.toFixed(1)} anos`, 114, y + 19);
  y += 34;

  // Dados de entrada
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark as [number,number,number]);
  doc.text('Dados da Simulação', 14, y);
  y += 8;

  const rows = [
    ['Valor da Máquina', fmt(parseFloat(formData.machineValue))],
    ['Valor da Reforma', fmt(parseFloat(formData.reformValue) || 0)],
    ['Taxa de Juros', `${formData.interestRate}% ao ano`],
    ['Prazo', `${formData.months} meses (${(parseInt(formData.months)/12).toFixed(0)} anos)`],
  ];

  rows.forEach(([label, value], i) => {
    if (i % 2 === 0) {
      doc.setFillColor(...lightGray as [number,number,number]);
      doc.rect(14, y - 4, 182, 10, 'F');
    }
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray as [number,number,number]);
    doc.text(label, 20, y + 3);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark as [number,number,number]);
    doc.text(value, 140, y + 3);
    y += 11;
  });
  y += 8;

  // Estrutura BNDES
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...green as [number,number,number]);
  doc.text('Estrutura BNDES Automático', 14, y);
  y += 7;
  const bndesInfo = [
    '• Financiamento: 70% do valor total para o bem (máquina/equipamento)',
    '• Capital de Giro: 30% do valor total associado ao investimento',
    '• Prazo: até 120 meses (10 anos) com carência de até 24 meses',
    '• Elegível: máquinas nacionais cadastradas no CFI/BNDES',
    '• Acesso: via banco agente credenciado (BB, Caixa, Bradesco, Itaú, etc.)',
  ];
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray as [number,number,number]);
  bndesInfo.forEach(line => { doc.text(line, 14, y); y += 6; });

  y += 6;

  // Próximos passos
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark as [number,number,number]);
  doc.text('Próximos Passos', 14, y);
  y += 7;
  const steps = [
    '1. Organize a documentação necessária (use o Checklist no app)',
    '2. Verifique se a máquina está cadastrada no CFI do BNDES',
    '3. Leve esta simulação ao banco agente de sua preferência',
    '4. Solicite aprovação formal do BNDES Automático',
  ];
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray as [number,number,number]);
  steps.forEach(step => { doc.text(step, 14, y); y += 6; });

  // Footer
  doc.setFillColor(...green as [number,number,number]);
  doc.rect(0, 280, 210, 17, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Inteligência Financeira — inteligenciafinanceira.ia.br', 14, 288);
  doc.text('Este documento é uma simulação estimativa. Consulte seu banco agente para condições definitivas.', 14, 293);

  doc.save(`simulacao-bndes-${Date.now()}.pdf`);
}

interface SimulationResult {
  totalValue: number;
  financingValue: number;
  workingCapitalValue: number;
  monthlyPayment: number;
  totalInterest: number;
  paybackPeriod: number;
}

const inputStyle = {
  width: '100%', padding: '12px 16px', border: '1px solid #e5e7eb',
  borderRadius: 10, fontSize: 15, outline: 'none', background: '#f9fafb',
  boxSizing: 'border-box' as const, fontFamily: 'system-ui, sans-serif',
  transition: 'border-color 0.15s',
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default function Simulator() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ machineValue: '', reformValue: '', interestRate: '11.50', months: '120' });
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Carrega jsPDF dinamicamente
  React.useEffect(() => {
    if (!(window as any).jspdf) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Cálculo local — não depende do backend
      const machineValue = parseFloat(formData.machineValue);
      const reformValue = parseFloat(formData.reformValue) || 0;
      const interestRate = parseFloat(formData.interestRate);
      const months = parseInt(formData.months);

      const totalValue = machineValue + reformValue;
      const financingValue = totalValue * 0.70;
      const workingCapitalValue = totalValue * 0.30;
      const monthlyRate = interestRate / 100 / 12;
      const monthlyPayment = monthlyRate === 0
        ? financingValue / months
        : (financingValue * (monthlyRate * Math.pow(1 + monthlyRate, months))) / (Math.pow(1 + monthlyRate, months) - 1);
      const totalInterest = monthlyPayment * months - financingValue;
      const paybackPeriod = months / 12;

      setResult({ totalValue, financingValue, workingCapitalValue, monthlyPayment, totalInterest, paybackPeriod });

      // Salva no backend em background (não bloqueia o resultado)
      api.createSimulation({ machineValue, reformValue, interestRate, months }).catch(() => {});

    } catch (error) {
      console.error('Calculation error:', error);
      alert('Erro ao calcular. Verifique os valores informados.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    setIsSaving(true);
    try {
      await api.createSimulation({
        machineValue: parseFloat(formData.machineValue),
        reformValue: parseFloat(formData.reformValue) || 0,
        interestRate: parseFloat(formData.interestRate),
        months: parseInt(formData.months),
      });
      alert('Simulação salva com sucesso!');
    } catch (error) {
      alert('Erro ao salvar simulação');
    } finally {
      setIsSaving(false);
    }
  };

  const fmt = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={14} /> Voltar
          </button>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>Simulador de Financiamento</h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>BNDES Automático</p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

          {/* Formulário */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calculator size={20} color="#059669" /> Dados de Entrada
            </h2>
            <form onSubmit={handleCalculate} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={labelStyle}>Valor da Máquina (R$)</label>
                <input type="number" name="machineValue" value={formData.machineValue} onChange={handleChange}
                  placeholder="Ex: 100.000" step="1000" required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#059669'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
              <div>
                <label style={labelStyle}>Valor da Reforma (opcional)</label>
                <input type="number" name="reformValue" value={formData.reformValue} onChange={handleChange}
                  placeholder="Ex: 20.000" step="1000" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#059669'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
              <div>
                <label style={labelStyle}>Taxa de Juros (% ao ano)</label>
                <input type="number" name="interestRate" value={formData.interestRate} onChange={handleChange}
                  placeholder="11.50" step="0.01" required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#059669'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
              <div>
                <label style={labelStyle}>Prazo</label>
                <select name="months" value={formData.months}
                  onChange={e => setFormData(prev => ({ ...prev, months: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="12">12 meses (1 ano)</option>
                  <option value="24">24 meses (2 anos)</option>
                  <option value="36">36 meses (3 anos)</option>
                  <option value="48">48 meses (4 anos)</option>
                  <option value="60">60 meses (5 anos)</option>
                  <option value="84">84 meses (7 anos)</option>
                  <option value="120">120 meses (10 anos)</option>
                </select>
              </div>
              <button type="submit" disabled={isLoading}
                style={{ background: isLoading ? '#d1d5db' : 'linear-gradient(135deg, #059669, #0891b2)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer', marginTop: 4 }}>
                {isLoading ? 'Calculando...' : '⚡ Calcular Financiamento'}
              </button>
            </form>

            {/* Info Box */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14, padding: '16px', marginTop: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#065f46', margin: '0 0 10px' }}>Estrutura BNDES Automático</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['70% do valor → Financiamento do bem', '30% do valor → Capital de giro associado', 'Prazo: até 120 meses (10 anos)', 'Máquinas nacionais cadastradas no CFI'].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#065f46' }}>
                    <span style={{ color: '#059669', fontWeight: 700 }}>•</span> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Resultado */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {result ? (
              <>
                <div style={{ background: '#fff', border: '2px solid #059669', borderRadius: 20, padding: '28px', boxShadow: '0 4px 12px rgba(5,150,105,0.15)' }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrendingUp size={20} color="#059669" /> Resultado da Simulação
                  </h2>

                  {/* Valor Total */}
                  <div style={{ background: '#f8fafc', borderRadius: 14, padding: '18px', marginBottom: 16, textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px' }}>Valor Total do Projeto</p>
                    <p style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: 0, letterSpacing: '-1px' }}>{fmt(result.totalValue)}</p>
                  </div>

                  {/* Financiamento e Capital */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: '#eff6ff', borderRadius: 12, padding: '14px', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, color: '#3b82f6', margin: '0 0 4px', fontWeight: 600 }}>Financiamento (70%)</p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8', margin: 0 }}>{fmt(result.financingValue)}</p>
                    </div>
                    <div style={{ background: '#ecfeff', borderRadius: 12, padding: '14px', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, color: '#06b6d4', margin: '0 0 4px', fontWeight: 600 }}>Capital de Giro (30%)</p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: '#0891b2', margin: 0 }}>{fmt(result.workingCapitalValue)}</p>
                    </div>
                  </div>

                  {/* Parcela */}
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14, padding: '18px', textAlign: 'center', marginBottom: 16 }}>
                    <p style={{ fontSize: 13, color: '#059669', margin: '0 0 4px', fontWeight: 600 }}>Parcela Mensal Estimada</p>
                    <p style={{ fontSize: 32, fontWeight: 800, color: '#065f46', margin: 0, letterSpacing: '-1px' }}>{fmt(result.monthlyPayment)}</p>
                  </div>

                  {/* Juros e Payback */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px', fontWeight: 600 }}>Total de Juros</p>
                      <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: 0 }}>{fmt(result.totalInterest)}</p>
                    </div>
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px', fontWeight: 600 }}>Payback</p>
                      <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: 0 }}>{result.paybackPeriod.toFixed(1)} anos</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={handleSave} disabled={isSaving}
                      style={{ flex: 1, background: '#059669', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Save size={15} /> {isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button onClick={() => generateSimulationPDF(formData, result)}
                      style={{ flex: 1, background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Download size={15} /> Baixar PDF
                    </button>
                  </div>
                </div>

                {/* Próximos passos */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>📋 Próximos Passos</h3>
                  {['Organize a documentação necessária', 'Consulte o checklist de documentos', 'Leve a simulação ao banco agente', 'Solicite aprovação do BNDES Automático'].map((step, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 14, color: '#374151' }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#059669', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                      {step}
                    </div>
                  ))}
                  <button onClick={() => navigate('/checklist')} style={{ width: '100%', background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', borderRadius: 10, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>
                    Ver Checklist de Documentos →
                  </button>
                </div>
              </>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Calculator size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
                <p style={{ fontSize: 16, color: '#6b7280', margin: '0 0 8px', fontWeight: 500 }}>Preencha os dados ao lado</p>
                <p style={{ fontSize: 14, color: '#9ca3af', margin: 0 }}>e clique em Calcular para ver a simulação</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}